import { useCallback } from 'react'
import {
  useReportBasicResultMutation,
  useReportRPUnitMutation,
  useReportMedicationMutation,
  useReportRPIndexMutation,
  useReportSpecificErrorMutation,
} from '../../../features/prescription'
import {
  BasicResult,
  Prescription,
  RPUnit,
  Medication,
} from '../../../types/models/Presc'

type UseReportCallbacksParams = {
  prescription: Prescription | undefined
  onReportSpecificErrorSuccess?: () => void
}

/**
 * 処方箋報告コールバックを提供する共通フック
 *
 * 各アプリ（MediLab, TMN, Melphin, Annotator）で共通のレポート機能を提供する。
 * prescription が undefined の場合、各コールバックは何もせずに return する。
 *
 * @example
 * ```tsx
 * const {
 *   reportBasicResult,
 *   reportRPUnit,
 *   reportMedication,
 *   reportRPIndex,
 *   reportSpecificError,
 *   isSending,
 * } = useReportCallbacks({
 *   prescription: selectedPresc,
 *   onReportSpecificErrorSuccess: () => {
 *     setIsModalOpen(false)
 *     setIsSnackAlertOpen(true)
 *   },
 * })
 * ```
 */
export const useReportCallbacks = ({
  prescription,
  onReportSpecificErrorSuccess,
}: UseReportCallbacksParams) => {
  // Mutation hooks
  const reportBasicResultMutation = useReportBasicResultMutation()
  const reportRPUnitMutation = useReportRPUnitMutation()
  const reportMedicationMutation = useReportMedicationMutation()
  const reportRPIndexMutation = useReportRPIndexMutation()
  const reportSpecificErrorMutation = useReportSpecificErrorMutation()
  // いずれかの Mutation が実行中かどうか
  const isSending =
    reportBasicResultMutation.isPending ||
    reportRPUnitMutation.isPending ||
    reportMedicationMutation.isPending ||
    reportRPIndexMutation.isPending ||
    reportSpecificErrorMutation.isPending

  const reportBasicResult = useCallback(
    async (key: keyof BasicResult, isReported: boolean) => {
      if (!prescription) return
      try {
        await reportBasicResultMutation.mutateAsync({
          prescId: prescription.id,
          key,
          isReported,
        })
      } catch (e) {
        console.log(e)
      }
    },
    [prescription, reportBasicResultMutation]
  )

  const reportRPUnit = useCallback(
    async (rpNum: number, key: keyof RPUnit, isReported: boolean) => {
      if (!prescription) return
      try {
        await reportRPUnitMutation.mutateAsync({
          prescId: prescription.id,
          rpNum,
          key,
          isReported,
        })
      } catch (e) {
        console.log(e)
      }
    },
    [prescription, reportRPUnitMutation]
  )

  const reportMedication = useCallback(
    async (
      rpNum: number,
      rpNumSub: number,
      key: keyof Medication,
      isReported: boolean
    ) => {
      if (!prescription) return
      try {
        await reportMedicationMutation.mutateAsync({
          prescId: prescription.id,
          rpNum,
          rpNumSub,
          key,
          isReported,
        })
      } catch (e) {
        console.log(e)
      }
    },
    [prescription, reportMedicationMutation]
  )

  const reportRPIndex = useCallback(
    async (rpNum: number, isReported: boolean) => {
      if (!prescription) return
      try {
        await reportRPIndexMutation.mutateAsync({
          prescId: prescription.id,
          rpNum,
          isReported,
        })
      } catch (e) {
        console.log(e)
      }
    },
    [prescription, reportRPIndexMutation]
  )

  const reportSpecificError = useCallback(
    async (ocrErrorLabel: string) => {
      if (!prescription) return
      try {
        await reportSpecificErrorMutation.mutateAsync({
          prescId: prescription.id,
          ocrErrorLabel,
        })
        onReportSpecificErrorSuccess?.()
      } catch (e) {
        console.log(e)
      }
    },
    [prescription, reportSpecificErrorMutation, onReportSpecificErrorSuccess]
  )

  return {
    reportBasicResult,
    reportRPUnit,
    reportMedication,
    reportRPIndex,
    reportSpecificError,
    isSending,
  }
}
